// ---- Workflow Manifest (lightweight, fetched once per session) ----

export type NotServicedPageConfig = {
  title: string;
  description?: string;
  primaryButton: { text: string; url: string };
  secondaryButton: { text: string; url: string };
};

export type WorkflowManifest = {
  id: string;
  version: number;
  sharedSteps?: {
    getStarted: StepManifestEntry;
    confirmation?: StepManifestEntry; // deprecated — confirmation is now per-category
    notServicedJunk?: NotServicedPageConfig;
    notServicedMoving?: NotServicedPageConfig;
  };
  serviceCategories: Record<string, StepManifestEntry[]>;
};

export type StepManifestEntry = {
  stepId: string;
  path: string;
  /**
   * Booking tracks this step belongs to ("standard" | "estimate"). Empty/
   * absent means the step shows on every track. See apps/sanity-studio's
   * workflowStep.ts (`tracksField`) for the authoring side.
   */
  tracks?: string[];
};

// ---- Step Config (fetched per page, on demand) ----

export type StepConfig = {
  stepId: string;
  title: string;
  titleVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleAlign?: "left" | "center" | "right";
  subtitle?: string;
  description?: string;
  tip?: string;
  hideTip?: boolean;
  tipIcon?: string;
  noteTextVariant?: "body1" | "body2";
  pageName?: string;
  icon?: string;
  fields?: FieldSchema[];
  contentBlocks?: ContentBlock[];
  validation?: Record<string, ValidationRule>;
  nextButtonText?: string;
  tooltipHeaderText?: string;
  textBlocks?: Record<string, string>;
  customMaxWidth?: number;
};

// ---- Content Blocks (ordered page layout) ----

// ---- Typed Text Blocks (per consumer) ----

/** Text blocks for the address field's "I can't find my address" link */
export type AddressTextBlocks = {
  cantFindAddress?: string;
};

/** Text blocks for the promo-code field's reveal link + invalid message */
export type PromoCodeTextBlocks = {
  promoCodeRevealText?: string;
  promoCodeInvalidText?: string;
};

/** Text blocks for the SMS consent checkbox section */
export type SmsConsentTextBlocks = {
  smsConsentPrefix?: string;
  smsConsentLinkText?: string;
  smsConsentSuffix?: string;
};

/** Text blocks for the availability/calendar form */
export type AvailabilityFormTextBlocks = {
  arrivalWindowHelperText?: string;
  noAvailabilityMessage?: string;
};

/** Text blocks for the pricing/truck loads display */
export type PricingListTextBlocks = {
  pricingDescription1?: string;
  pricingDescription2?: string;
};

/**
 * One CMS-authored truck-load card on the junk pricing step.
 *
 * `priceTemplate` is a Liquid template — `{{ price_… }}` variables resolve to
 * the location's live junk-removal price points (see buildPriceVariables in
 * entities/pricing). Plain text renders as-is, which doubles as a manual
 * price override. A card is hidden for locations missing any variable the
 * template prints unconditionally (see requiredVariables in shared/libs/liquid).
 */
export type PricingCardConfig = {
  /** Resolved Sanity CDN URL of the truck graphic */
  imageUrl?: string;
  /** Card label on desktop */
  name: string;
  /** Card label on mobile; falls back to `name` */
  shortName?: string;
  /** Liquid template for the price line, e.g. "approx. {{ price_1_2_truckload }}" */
  priceTemplate?: string;
};

// ---- Content Blocks (ordered page layout) ----

export type SmsConsentContentBlock = {
  blockType: "sms-consent";
  key: string;
  textBlocks?: SmsConsentTextBlocks;
};

/**
 * Self-contained dwelling information block.
 *
 * Renders the dwelling-type tile-select AND the dependent floor/access-type
 * selects as a single atomic unit. Keeping them in one block prevents CMS
 * editors from accidentally splitting the tile-select from the floor picker
 * (which would break the reactive floor options).
 */
export type DwellingInfoContentBlock = {
  blockType: "dwelling-info";
  key: string;
  source: "origin" | "destination";
};

/** Displays a stored address (origin or destination) as a read-only line */
export type AddressDisplayContentBlock = {
  blockType: "address-display";
  key: string;
  source: "origin" | "destination";
};

export type ContentBlock =
  | FieldContentBlock
  | TextContentBlock
  | DividerContentBlock
  | NoteContentBlock
  | ComponentContentBlock
  | SmsConsentContentBlock
  | DwellingInfoContentBlock
  | AddressDisplayContentBlock;

export type FieldContentBlock = FieldSchema & {
  blockType: "field";
};

export type TextContentBlock = {
  blockType: "text";
  key: string;
  content: string;
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body1"
    | "body2"
    | "subtitle1"
    | "subtitle2"
    | "caption";
  align?: "left" | "center" | "right";
  html?: boolean;
};

export type DividerContentBlock = {
  blockType: "divider";
  key: string;
};

export type NoteContentBlock = {
  blockType: "note";
  key: string;
  content: string;
  icon?: string;
};

export type ComponentContentBlock = {
  blockType: "component";
  componentKey: string;
  key: string;
  textBlocks?: AvailabilityFormTextBlocks & PricingListTextBlocks;
  /** Component-specific field definitions (e.g., service options, form fields).
   *  Merged into stepConfig.fields by useStepConfig so components can read them transparently. */
  fields?: FieldSchema[];
  /** pricing-list only: truck cards per row on desktop (2–4). Empty → 3. */
  columns?: number;
  /** pricing-list only: CMS-authored cards. Empty → built-in fallback set. */
  cards?: PricingCardConfig[];
};

// ---- Field Schema ----

export type FieldSchema = {
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  defaultValue?: unknown;
  options?: FieldOption[];
  validation?: ValidationRule;
  gridSize?: { xs?: number; sm?: number; md?: number };
  textBlocks?: AddressTextBlocks & PromoCodeTextBlocks;
  /** promo-code only: skip the on-Next validation and let the user through. */
  skipValidation?: boolean;
};

export type FieldType =
  | "text"
  | "email"
  | "phone"
  | "select"
  | "checkbox"
  | "radio"
  | "date"
  | "time"
  | "number"
  | "textarea"
  | "address"
  | "tile-select"
  | "increment"
  | "calendar"
  | "postal-input"
  | "promo-code"
  | "hidden";

export type FieldOption = {
  value: string;
  label: string;
  icon?: string;
  subLabel?: string;
  ctaText?: string;
};

// ---- Validation ----

export type ValidationRule = {
  type:
    | "required"
    | "email"
    | "phone"
    | "postal"
    | "minLength"
    | "maxLength"
    | "pattern";
  message?: string;
  value?: string | number;
};

// ---- Helpers ----

/**
 * Extract FieldSchema[] from a StepConfig, regardless of whether
 * fields are defined via `fields` or `contentBlocks`.
 */
export const getFieldsFromConfig = (config: StepConfig): FieldSchema[] => {
  if (config.contentBlocks?.length) {
    return config.contentBlocks.flatMap((b): FieldSchema[] => {
      if (b.blockType === "field") {
        const { blockType: _bt, ...field } = b;
        return [field];
      }
      // dwelling-info implicitly owns typeOfDwelling + floor + accessType fields.
      // The source prefix ("origin" | "destination") determines field names.
      if (b.blockType === "dwelling-info") {
        const prefix = b.source;
        return [
          {
            name: `${prefix}TypeOfDwelling`,
            type: "tile-select" as const,
            label: "Type of dwelling",
            required: true,
          },
          {
            name: `${prefix}Floor`,
            type: "select" as const,
            label: "Other details",
            required: true,
            defaultValue: "1",
          },
          {
            name: `${prefix}AccessType`,
            type: "select" as const,
            label: "Access type",
            required: true,
          },
        ];
      }
      return [];
    });
  }
  return config.fields ?? [];
};

/**
 * Returns sub-field descriptors for a composite `address` field.
 *
 * When baseName is "address" (legacy), returns the original unprefixed names
 * (address, address2, city, state, postal, country).
 *
 * For any other baseName (e.g. "origin", "destination"), returns prefixed names:
 * origin, origin2, originCity, originState, originPostal, originCountry.
 */
export const getAddressSubFields = (
  baseName: string,
): Array<{ name: string; label: string; required: boolean }> => {
  if (!baseName || baseName === "address") {
    return [
      { name: "address", label: "Address", required: true },
      { name: "address2", label: "Apartment/suite", required: false },
      { name: "city", label: "City", required: true },
      { name: "state", label: "State/province", required: true },
      { name: "postal", label: "Zip/postal code", required: true },
      { name: "country", label: "Country", required: false },
    ];
  }
  return [
    { name: baseName, label: "Address", required: true },
    { name: `${baseName}2`, label: "Apartment/suite", required: false },
    { name: `${baseName}City`, label: "City", required: true },
    { name: `${baseName}State`, label: "State/province", required: true },
    { name: `${baseName}Postal`, label: "Zip/postal code", required: true },
    { name: `${baseName}Country`, label: "Country", required: false },
  ];
};

/**
 * Expands field names for form registration, replacing composite fields
 * (like address) with their sub-field names.
 */
export const expandFieldNamesForForm = (fields: FieldSchema[]): string[] => {
  const names: string[] = [];
  for (const field of fields) {
    if (field.type === "address") {
      for (const sub of getAddressSubFields(field.name)) {
        names.push(sub.name);
      }
    } else if (field.type === "postal-input") {
      // postal-input registers servicePostal (gate field, has a store binding).
      // _postalDisplay is managed internally by PostalInputBlock via useController.
      names.push("servicePostal");
    } else {
      names.push(field.name);
    }
  }
  return names;
};

/**
 * Expands required field labels for empty-field tracking,
 * replacing composite fields with their required sub-field labels.
 */
export const expandRequiredFieldLabels = (
  fields: FieldSchema[],
): Record<string, string> => {
  const labels: Record<string, string> = {};
  for (const field of fields) {
    if (field.type === "address") {
      if (field.required) {
        for (const sub of getAddressSubFields(field.name)) {
          if (sub.required) {
            labels[sub.name] = sub.label;
          }
        }
      }
    } else if (field.type === "postal-input") {
      // servicePostal is always required — it gates progress until the postal API confirms.
      labels["servicePostal"] = field.label || "Postal / Zip Code";
    } else if (field.required) {
      labels[field.name] = field.label || field.name;
    }
  }
  return labels;
};
